import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Avatar,
  Badge,
  Tooltip,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";
import EmpresaSelector from "@components/layout/EmpresaSelector";
import ThemeModeToggle from "@components/common/ThemeModeToggle";
import { useEmpresa } from "@contexts/EmpresaContext";
import { useEmpresaLogoUrl } from "@hooks/useEmpresaLogoUrl";
import { normalizeHttpUrl } from "@utils/safeUrl";
import {
  ID_MENU_LATERAL,
  ID_MENU_LATERAL_MOBILE,
  MENU_EXPANDIDO,
  MENU_OCULTO,
} from "@components/layout/menuLateral";

// O que o próximo clique faz, por estado: o botão percorre expandido → rail →
// oculto → expandido. O NOME acessível é fixo ("Menu lateral") e o estado vai em
// aria-expanded; o texto abaixo é só a dica visual da ação.
const ACAO_DO_MENU = {
  expandido: "Recolher menu lateral",
  rail: "Ocultar menu lateral",
  oculto: "Mostrar menu lateral",
};

// Texto da marca quando a empresa ativa não tem logo utilizável.
const MARCA_PADRAO = "Ag Festas";

const TopBar = ({
  onOpenDrawer,
  onOpenNotifications,
  onOpenProfile,
  onToggleCollapse,
  estadoMenu,
  mobileOpen,
  drawerWidth,
  avatarUrl,
  avatarFallback,
}) => {
  const theme = useTheme();
  const iconButtonStyles = {
    backgroundColor: alpha(theme.palette.common.white, 0.12),
    border: `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
    transition: "transform 0.2s ease, background-color 0.2s ease",
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.white, 0.2),
      transform: "translateY(-1px)",
    },
  };

  const { empresaAtiva } = useEmpresa();
  const logoEnviado = useEmpresaLogoUrl({
    empresaId: empresaAtiva?.id,
    versao: empresaAtiva?.logo_at,
    ativo: Boolean(empresaAtiva?.tem_logo),
  });
  // Falha pertence à empresa ativa: outra empresa pode usar a mesma URL.
  const [srcComFalha, setSrcComFalha] = useState(null);
  const falhouNestaEmpresa = srcComFalha?.empresaId === empresaAtiva?.id;

  const urlExterna = normalizeHttpUrl(empresaAtiva?.logo_url);
  const srcBlob =
    logoEnviado.status === "ready" &&
    !(falhouNestaEmpresa && logoEnviado.url === srcComFalha.url)
      ? logoEnviado.url
      : null;
  const srcExterna =
    urlExterna && !(falhouNestaEmpresa && urlExterna === srcComFalha.url)
      ? urlExterna
      : null;
  // Logo enviado tem prioridade; enquanto ele carrega, não mostra o externo.
  const aguardandoLogo =
    Boolean(empresaAtiva?.tem_logo) && logoEnviado.status === "loading";
  const srcMarca = srcBlob ?? (aguardandoLogo ? null : srcExterna);

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        zIndex: theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 64, md: 72 },
          px: { xs: 2, md: 3 },
        }}
      >
        <Tooltip title="Abrir menu de navegacao">
          <IconButton
            color="inherit"
            edge="start"
            onClick={onOpenDrawer}
            aria-label="Abrir menu de navegacao"
            aria-expanded={Boolean(mobileOpen)}
            aria-controls={ID_MENU_LATERAL_MOBILE}
            sx={{ mr: 2, display: { md: "none" }, ...iconButtonStyles }}
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title={ACAO_DO_MENU[estadoMenu]}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={onToggleCollapse}
            aria-label="Menu lateral"
            aria-expanded={estadoMenu === MENU_EXPANDIDO}
            aria-controls={ID_MENU_LATERAL}
            aria-describedby="menu-lateral-acao"
            sx={{
              mr: 2,
              display: { xs: "none", md: "inline-flex" },
              ...iconButtonStyles,
            }}
          >
            {estadoMenu === MENU_OCULTO ? (
              <MenuIcon />
            ) : (
              <MenuOpenIcon
                sx={{
                  transform:
                    estadoMenu === MENU_EXPANDIDO ? "none" : "rotate(180deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            )}
          </IconButton>
        </Tooltip>
        {/* aria-expanded só diz aberto/fechado: rail e oculto são ambos "fechado".
            A descrição (o que o próximo clique faz) separa os dois para quem usa
            leitor de tela; a dica visual do Tooltip não é lida porque o botão já
            tem aria-label. */}
        <Box
          component="span"
          id="menu-lateral-acao"
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
          }}
        >
          {ACAO_DO_MENU[estadoMenu]}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexShrink: 1,
            minWidth: 0,
            height: 40,
            maxWidth: { xs: 160, md: 240 },
            mr: 2,
          }}
        >
          {srcMarca && (
            <Box
              component="img"
              src={srcMarca}
              alt={empresaAtiva?.nome || MARCA_PADRAO}
              referrerPolicy={srcBlob ? undefined : "no-referrer"}
              onError={() =>
                setSrcComFalha({ empresaId: empresaAtiva?.id, url: srcMarca })
              }
              sx={{ height: 40, maxWidth: "100%", objectFit: "contain" }}
            />
          )}
          {!srcMarca && aguardandoLogo && (
            <Skeleton
              variant="rectangular"
              width={112}
              height={40}
              aria-hidden="true"
              sx={{
                borderRadius: 1,
                bgcolor: alpha(theme.palette.common.white, 0.16),
              }}
            />
          )}
          {!srcMarca && !aguardandoLogo && (
            <Typography
              component="span"
              noWrap
              sx={{ fontWeight: 700, fontSize: "1.125rem", letterSpacing: 0.2 }}
            >
              {MARCA_PADRAO}
            </Typography>
          )}
        </Box>

        {/* Sem título aqui: o título da página é o h1 do PageHeader (uma só vez).
            O espaçador empurra os controles para a direita. */}
        <Box sx={{ flexGrow: 1 }} />

        <EmpresaSelector />

        <ThemeModeToggle sx={{ mr: 1 }} />

        <Tooltip title="Notificações">
          <IconButton
            color="inherit"
            onClick={onOpenNotifications}
            aria-label="Abrir notificacoes"
            sx={{ mr: 1, ...iconButtonStyles }}
          >
            <Badge badgeContent={0} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        <Tooltip title="Conta">
          <IconButton
            onClick={onOpenProfile}
            color="inherit"
            aria-label="Abrir menu da conta"
            aria-haspopup="menu"
            sx={iconButtonStyles}
          >
            <Avatar
              src={avatarUrl}
              alt=""
              sx={{
                width: 32,
                height: 32,
                bgcolor: avatarUrl
                  ? "transparent"
                  : theme.palette.secondary.main,
                fontSize: "0.875rem",
                border: `1px solid ${alpha(theme.palette.common.white, 0.4)}`,
              }}
              imgProps={{
                loading: "lazy",
                alt: "",
                onError: (event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = "";
                },
                style: { objectFit: "cover" },
              }}
            >
              {avatarFallback}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
